import {
  type Color,
  type ColorSpace,
  clampToGamut,
  color,
  hueDistance,
  maxChroma,
  oklchToRgb255,
  toCss,
} from "./color";

export const MAX_CHROMA = 0.4;
const MAX_HUE = 360;
const PADDING = 2;

export interface Dimensions {
  height: number;
  width: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface Rect extends Point, Dimensions {}

const plotSize = (dimensions: Dimensions): Dimensions => ({
  height: dimensions.height - PADDING * 2,
  width: dimensions.width - PADDING * 2,
});

export const fractionToPixel = (
  fx: number,
  fy: number,
  dimensions: Dimensions
): Point => {
  const plot = plotSize(dimensions);
  return {
    x: PADDING + fx * plot.width,
    y: PADDING + (1 - fy) * plot.height,
  };
};

export const toPixel = (l: number, c: number, dimensions: Dimensions): Point =>
  fractionToPixel(c / MAX_CHROMA, l, dimensions);

export const fieldRect = (
  dimensions: Dimensions,
  xSteps: number,
  ySteps: number
): Rect => {
  const plot = plotSize(dimensions);
  const cellWidth = plot.width / xSteps;
  const cellHeight = plot.height / ySteps;

  return {
    height: plot.height + cellHeight,
    width: plot.width + cellWidth,
    x: PADDING - cellWidth / 2,
    y: PADDING - cellHeight / 2,
  };
};

export const toLC = (
  x: number,
  y: number,
  dimensions: Dimensions
): { l: number; c: number } => {
  const plot = plotSize(dimensions);
  return {
    c: Math.max(
      0,
      Math.min(MAX_CHROMA, ((x - PADDING) / plot.width) * MAX_CHROMA)
    ),
    l: Math.max(0, Math.min(1, 1 - (y - PADDING) / plot.height)),
  };
};

type Sample = (fx: number, fy: number) => { r: number; g: number; b: number };

export const fieldPixels = (
  xSteps: number,
  ySteps: number,
  sample: Sample
): {
  pixels: Uint8ClampedArray<ArrayBuffer>;
  width: number;
  height: number;
} => {
  const width = xSteps + 1;
  const height = ySteps + 1;
  const pixels = new Uint8ClampedArray(new ArrayBuffer(width * height * 4));

  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      const { r, g, b } = sample(col / xSteps, 1 - row / ySteps);
      const offset = (row * width + col) * 4;
      pixels[offset] = r;
      pixels[offset + 1] = g;
      pixels[offset + 2] = b;
      pixels[offset + 3] = 255;
    }
  }

  return { height, pixels, width };
};

export const GAMUT_VIEW = { height: 500, width: 1000 };
const FIELD_STEPS = 50;
const OUTLINE_STEPS = 100;
const HUE_MATCH_TOLERANCE = 0.005;

export interface Field {
  height: number;
  pixels: Uint8ClampedArray<ArrayBuffer>;
  rect: Rect;
  width: number;
}

const field = (sample: Sample): Field => ({
  ...fieldPixels(FIELD_STEPS, FIELD_STEPS, sample),
  rect: fieldRect(GAMUT_VIEW, FIELD_STEPS, FIELD_STEPS),
});

const round = (value: number) => Math.round(value * 100) / 100;

const polyline = (points: Point[]): string =>
  points
    .map(
      (point, index) =>
        `${index === 0 ? "M" : "L"} ${round(point.x)} ${round(point.y)}`
    )
    .join(" ");

export function gamutField(hue: number): Field {
  return field((fc, fl) => oklchToRgb255(fl, fc * MAX_CHROMA, hue));
}

export function gamutOutline(hue: number, space: ColorSpace): string {
  return polyline(
    Array.from({ length: OUTLINE_STEPS + 1 }, (_, i) => {
      const l = i / OUTLINE_STEPS;
      return toPixel(l, maxChroma(l, hue, space), GAMUT_VIEW);
    })
  );
}

export function chromaField(lightness: number): Field {
  return field((fh, fc) =>
    oklchToRgb255(lightness, fc * MAX_CHROMA, fh * MAX_HUE)
  );
}

export function chromaOutline(lightness: number, space: ColorSpace): string {
  return polyline(
    Array.from({ length: OUTLINE_STEPS + 1 }, (_, i) => {
      const hue = (i / OUTLINE_STEPS) * MAX_HUE;
      return fractionToPixel(
        hue / MAX_HUE,
        maxChroma(lightness, hue, space) / MAX_CHROMA,
        GAMUT_VIEW
      );
    })
  );
}

const AXIS_MAX: Record<keyof Color, number> = {
  c: MAX_CHROMA,
  h: MAX_HUE,
  l: 1,
};

export function axisGradient(
  axis: keyof Color,
  base: Color,
  stops = 16
): string {
  const colors = Array.from({ length: stops + 1 }, (_, i) =>
    toCss({ ...base, [axis]: (i / stops) * AXIS_MAX[axis] })
  );
  return `linear-gradient(in oklch to right, ${colors.join(", ")})`;
}

export function gamutMarker(hue: number, selected: Color) {
  return hueDistance(selected.h, hue) < HUE_MATCH_TOLERANCE
    ? toPixel(selected.l, selected.c, GAMUT_VIEW)
    : null;
}

export function selectGamutPoint(
  hue: number,
  point: Point,
  viewport: Dimensions
): Color {
  const { l, c } = toLC(
    (point.x / viewport.width) * GAMUT_VIEW.width,
    (point.y / viewport.height) * GAMUT_VIEW.height,
    GAMUT_VIEW
  );
  return clampToGamut(color(l, c, hue), "p3");
}

export function moveGamutPoint(
  hue: number,
  selected: Color,
  key: string
): Color | null {
  const position = toPixel(selected.l, selected.c, GAMUT_VIEW);
  const plot = plotSize(GAMUT_VIEW);
  switch (key) {
    case "ArrowUp":
      position.y -= plot.height / 100;
      break;
    case "ArrowDown":
      position.y += plot.height / 100;
      break;
    case "ArrowLeft":
      position.x -= plot.width / 100;
      break;
    case "ArrowRight":
      position.x += plot.width / 100;
      break;
    case "Enter":
    case " ":
      break;
    default:
      return null;
  }
  return selectGamutPoint(hue, position, GAMUT_VIEW);
}
